// newsnetwide.com - Main Entry Point

import './styles/main.css';
import './styles/features.css';
import { initStore } from './store/state.js';
import { initGlobe } from './globe/GlobeScene.js';
import { initUI } from './ui/UIController.js';
import { initMarketClocks } from './ui/MarketClocks.js';
import { api } from './api/client.js';
import { initToday } from './features/today.js';
import { initAuth } from './features/auth.js';
import { applyStatic, initLanguagePicker } from './i18n/index.js';
import { initNavTools } from './ui/navTools.js';

// Initialize store (loads persisted prefs)
initStore();

// Import state and actions for use in modules
import { state, actions, getters } from './store/state.js';

// Global error handler
window.addEventListener('error', (event) => {
    console.error('[global] Uncaught error:', event.error);
    actions.setError('An unexpected error occurred. Please refresh the page.');
});

window.addEventListener('unhandledrejection', (event) => {
    console.error('[global] Unhandled rejection:', event.reason);
    actions.setError('A network error occurred. Please check your connection.');
});

// Small helpers that talk to the DOM directly — used to guarantee the loading
// overlay is dismissed even if the reactive UI watcher has not been installed
// yet (e.g. initGlobe() throws before initUI() runs).
function hideLoadingOverlay() {
    const el = document.querySelector('.loading-overlay');
    if (el) el.style.display = 'none';
}

function showFatalError(err) {
    hideLoadingOverlay();
    const errEl = document.querySelector('.error-state');
    if (!errEl) return;
    errEl.style.display = 'flex';
    const msgEl = errEl.querySelector('.error-message');
    if (msgEl) msgEl.textContent = err?.message || String(err) || 'Failed to initialize.';
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
    try {
        // Show loading state
        actions.setLoading(true);

        // Translate the page's fixed text and add the language menu (src/i18n)
        applyStatic();
        initLanguagePicker();
        initNavTools();

        // Install UI watchers FIRST so the loading overlay can be hidden
        // reactively even if a later step fails.
        initUI();
        initMarketClocks(); // header (phones); pure client-side, independent of API data
        initMarketClocks(document.getElementById('market-clocks-panel')); // right column (tablets and computers)

        // Initialize globe (Three.js scene)
        await initGlobe();

        // Load initial data
        await loadInitialData();

        // Engagement features: Today panel (top stories, central banks, your
        // countries) and free accounts. Both are independent of the globe.
        initToday({ onOpenCountry: (iso2) => actions.selectCountry(iso2), countries: state.countries });
        initAuth();

        // Deep link from a country page: /app?country=IT opens that country.
        const deepLink = new URLSearchParams(location.search).get('country');
        if (deepLink && /^[A-Za-z]{2}$/.test(deepLink)) actions.selectCountry(deepLink.toUpperCase());

        actions.setLoading(false);
        hideLoadingOverlay(); // belt-and-braces
        console.log('[app] Initialized successfully');
    } catch (err) {
        console.error('[app] Initialization failed:', err);
        actions.setLoading(false);
        actions.setError(err?.message || 'Failed to initialize application. Please refresh.');
        showFatalError(err);
    }
});

async function loadInitialData() {
    try {
        // Load countries and categories in parallel
        const [countriesRes, categoriesRes] = await Promise.all([
            api.getCountries({ limit: 250 }),
            api.getCategories({ active_only: true }),
        ]);

        actions.setCountries(countriesRes.data || []);
        actions.setCategories(categoriesRes.data || []);

        // Load latest news and breaking news
        const [latestRes, breakingRes] = await Promise.all([
            api.getLatestNews(20),
            api.getBreakingNews(10),
        ]);

        actions.setLatestNews(latestRes.data || []);
        actions.setBreakingNews(breakingRes.data || []);

        // Group news by country and category for quick access
        groupNewsByCountryAndCategory(latestRes.data || []);
        groupNewsByCountryAndCategory(breakingRes.data || []);
    } catch (err) {
        console.error('[app] Failed to load initial data:', err);
        // Don't throw - show error in UI but allow globe to work
        actions.setError('Some data could not be loaded. Globe is still interactive.');
    }
}

function groupNewsByCountryAndCategory(news) {
    for (const article of news) {
        const countryKey = article.country_iso2;
        if (countryKey) {
            const existing = state.news.byCountry.get(countryKey) || [];
            // Avoid duplicates by URL
            if (!existing.some((a) => a.url === article.url)) {
                existing.push(article);
                state.news.byCountry.set(countryKey, existing);
            }
        }

        const catKey = article.category_slug;
        if (catKey) {
            const existing = state.news.byCategory.get(catKey) || [];
            if (!existing.some((a) => a.url === article.url)) {
                existing.push(article);
                state.news.byCategory.set(catKey, existing);
            }
        }
    }
}

// Make store available globally for debugging
window.__NEWSNETWORK__ = { state, actions, getters, api };

export { state, actions, getters, api };
